import { Badge } from "@/common/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";
import { formatTimestamp } from "@/lib/utils";

import type { AuditEventEntry } from "./audit-log-types";

interface AuditLogTableProps {
  entries: readonly AuditEventEntry[];
  selectedEntry: AuditEventEntry | null;
  onSelect: (entry: AuditEventEntry) => void;
}

function actionBadgeVariant(action: string): "default" | "secondary" | "outline" {
  if (action.startsWith("auth.")) return "default";
  if (action.startsWith("session.")) return "secondary";
  return "outline";
}

function truncateId(id: string | null): string {
  if (!id) return "-";
  return id.length > 12 ? `${id.slice(0, 12)}...` : id;
}

function metadataPreview(metadata: Record<string, unknown>): string {
  const keys = Object.keys(metadata);
  if (keys.length === 0) return "-";
  return keys.slice(0, 3).join(", ") + (keys.length > 3 ? "..." : "");
}

export function AuditLogTable({
  entries,
  selectedEntry,
  onSelect,
}: AuditLogTableProps): React.ReactElement {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Time</TableHead>
          <TableHead>Actor</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Target Type</TableHead>
          <TableHead>Target ID</TableHead>
          <TableHead>Metadata</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry, index) => {
          const isSelected = selectedEntry?.id === entry.id;

          return (
            <TableRow
              key={entry.id}
              data-testid={`audit-row-${index}`}
              className={`cursor-pointer ${isSelected ? "bg-muted" : ""}`}
              tabIndex={0}
              role="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(entry)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(entry);
                }
              }}
            >
              <TableCell className="whitespace-nowrap text-xs">
                {formatTimestamp(entry.created_at)}
              </TableCell>
              <TableCell className="max-w-[150px] truncate font-mono text-xs">
                {truncateId(entry.actor_user_id)}
              </TableCell>
              <TableCell>
                <Badge variant={actionBadgeVariant(entry.action)}>{entry.action}</Badge>
              </TableCell>
              <TableCell className="text-xs">{entry.target_type ?? "-"}</TableCell>
              <TableCell className="max-w-[120px] truncate font-mono text-xs">
                {truncateId(entry.target_id)}
              </TableCell>
              <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">
                {metadataPreview(entry.metadata)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
